import { Marker, SingleMapMarker } from 'components/map/Map';
import EventUtils from 'helper/eventUtils';
import { transformToRioPosition } from 'helper/position';
import { getVehicleIdentifier } from 'helper/vehicleV2';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearSelectedVehicle,
    getSelectedVehicle,
    setFollowSelectedVehicle,
    setSelectedVehicle,
} from 'store/slices/appSlice';

import { DrivingState, type VehicleV2 } from 'API';

import './_styles.scss';

import { setMarkerColor } from './utils';

interface VehicleMarkerProps {
    vehicle: VehicleV2;
}

const TRUCK_ICON_NAMES = ['truck'];

const VehicleMarker = ({ vehicle }: VehicleMarkerProps) => {
    const dispatch = useDispatch();
    const selectedVehicle = useSelector(getSelectedVehicle);
    const selectedVIN = selectedVehicle?.selectedVIN;
    const isSelected = selectedVIN === vehicle?.vin;
    const position = vehicle.position?.value?.position;

    const vehicleIdentifier = useMemo(() => getVehicleIdentifier(vehicle), [vehicle]);
    const markerColor = useMemo(() => setMarkerColor(vehicle), [vehicle]);

    const handleClick = useCallback(() => {
        if (isSelected) {
            dispatch(clearSelectedVehicle());
            return;
        }

        dispatch(setSelectedVehicle({ selectedVIN: vehicle?.vin, pvlTriggered: false }));
        dispatch(setFollowSelectedVehicle(false));
    }, [dispatch, isSelected, vehicle?.vin]);

    const eventListenerMap = useMemo(() => ({ [EventUtils.TAP]: handleClick }), [handleClick]);

    const hasPosition = !!position;
    const latitude = position?.latitude;
    const longitude = position?.longitude;

    const markerPosition = useMemo(
        () => (hasPosition ? transformToRioPosition({ latitude, longitude }) : undefined),
        [hasPosition, latitude, longitude]
    );

    const bearing = position?.heading || undefined;
    const isMoving = vehicle.drivingState?.value?.state === DrivingState.DRIVING;

    const icon = useMemo(
        () => (
            <SingleMapMarker
                fixed
                bearing={bearing}
                moving={isMoving}
                // SingleMapMarker drops unknown props, so the test id and selection state sit on the name label.
                name={
                    <span
                        data-testid={`map-vehicle-marker-${vehicle.vin}`}
                        data-state={isSelected ? 'selected' : 'unselected'}
                    >
                        {vehicleIdentifier}
                    </span>
                }
                iconNames={TRUCK_ICON_NAMES}
                active={isSelected}
                className={markerColor}
            />
        ),
        [bearing, isMoving, vehicleIdentifier, isSelected, markerColor, vehicle.vin]
    );

    if (!markerPosition) {
        return null;
    }

    return <Marker eventListenerMap={eventListenerMap} position={markerPosition} icon={icon} />;
};

export default memo(VehicleMarker);
